// Экран «Открытые фронты» (2026-09-07): что можно брать в работу прямо сейчас.
// Строка попадает сюда, когда все действующие предшественники задачи закончены
// по факту (правило — в app/fronts_lib.py). Подрядчик видит только свои задачи,
// остальные роли — все, с фильтром по подрядчику в общей панели фильтров.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';
import { fmt } from '../util';

type Otvet = Data<'/api/projects/{pid}/fronty', 'get'>;
type Stroka = Otvet['stroki'][number];

const FAKT_ROLI = ['администратор', 'планер', 'подрядчик'];

function todayIso(): string {
  const d = new Date(), p2 = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p2(d.getMonth() + 1) + '-' + p2(d.getDate());
}

export function Fronts({ ctx }: { ctx: ShellCtx }) {
  const [data, setData] = useState<Otvet | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    const r = await call<Otvet>('get', p('/api/projects/{pid}/fronty', { pid: ctx.pid }));
    if (!r.ok) { ctx.onError?.(r.error.message); return; }
    setData(r.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  // фильтры общей панели: корпус, работа, этаж, подрядчик
  const rows = useMemo(() => {
    // ответ без списка (старая версия бэка) экран ронять не должен
    return (data?.stroki || []).filter(r =>
      (!ctx.matchesKorpus || ctx.matchesKorpus(r.korpus))
      && (!ctx.matchesTask || ctx.matchesTask(r)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ctx.filterKey]);

  const canFakt = FAKT_ROLI.includes(ctx.role || '');
  const vRabote = rows.filter(r => r.v_rabote).length;
  const pozdno = rows.filter(r => r.prosrocheno).length;

  /** отметить, что работы на фронте начались: статус и дата начала (если её нет) */
  async function nachat(r: Stroka) {
    setBusy(r.task_id);
    const body: { ids: number[]; revs: Record<number, number>; status: string; nachalo?: string } =
      { ids: [r.task_id], revs: { [r.task_id]: r.rev }, status: 'СМР начаты' };
    if (!r.vn) body.nachalo = todayIso();
    const res = await call('post', p('/api/projects/{pid}/fakt/bulk', { pid: ctx.pid }), body);
    setBusy(null);
    if (!res.ok) { ctx.onError?.(res.error.message); return; }
    ctx.onSaved?.('Отмечено начало работ.');
    setData(d => d && ({
      ...d,
      stroki: d.stroki.map(x => x.task_id === r.task_id
        ? { ...x, status: 'СМР начаты', v_rabote: true, vn: x.vn || todayIso() } : x),
    }));
  }

  return (
    <div>
      <div className="bar actbar">
        <span className="chk-b"><b>{rows.length}</b>открытых фронтов</span>
        <span className="chk-b"><b>{vRabote}</b>уже в работе</span>
        <span className="chk-b"><b>{pozdno}</b>просрочено по плану</span>
        <button className="sec" title="Перечитать список по текущему расчёту и факту"
          onClick={() => void load()}>Обновить</button>
        {data?.moy_podryadchik && (
          <span className="muted">Показаны задачи организации «{data.moy_podryadchik}».</span>
        )}
      </div>

      {data?.obrezano && (
        <div className="muted" style={{ marginBottom: 6 }}>
          Показаны первые {data.stroki?.length || 0} строк из {data.vsego} — сузьте фильтры сверху.
        </div>
      )}

      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th>Корпус</th><th>Эт.</th><th>Работа</th><th>Подрядчик</th>
              <th title="Информационные отметки ячейки: материал на площадке, замечания">Отметки</th>
              <th title="Когда закончился последний предшественник">Фронт открыт с</th>
              <th title="Сколько дней фронт стоит открытым">Дней</th>
              <th>План начала</th><th>План окончания</th><th>Дл.</th>
              <th>Состояние</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.task_id}>
                <td>{r.korpus}</td>
                <td className="num">{r.etazh}</td>
                <td className="pw-link" title="Открыть карточку работы"
                  onClick={() => ctx.onWorkCard?.(r.rabota)}>{r.rabota}</td>
                <td>{r.podryadchik || '—'}</td>
                <td>{(r.otmetki || []).join(', ')}</td>
                <td>{fmt(r.otkryt_s)}</td>
                <td className="num">{r.dney_otkryt ?? ''}</td>
                <td>{fmt(r.nachalo)}</td>
                <td style={r.prosrocheno ? { color: 'var(--plot-danger)' } : undefined}>
                  {fmt(r.okonchanie)}
                </td>
                <td className="num">{r.dlit ?? ''}</td>
                <td>{r.v_rabote ? 'в работе' : 'можно начинать'}</td>
                <td className="act" style={{ whiteSpace: 'nowrap' }}>
                  {canFakt && !r.v_rabote && (
                    <button className="sec" disabled={busy === r.task_id}
                      title="Поставить статус «СМР начаты» и сегодняшнюю дату начала"
                      onClick={() => void nachat(r)}>Начали</button>
                  )}
                  <a title="Открыть шахматку факта по этому корпусу"
                    style={{ cursor: 'pointer', marginLeft: 8 }}
                    onClick={() => ctx.onGoTab?.('fakt', r.korpus)}>в шахматку</a>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={12} className="muted" style={{ padding: 14 }}>
                Открытых фронтов нет: у всех незаконченных задач остались незакрытые
                предшественники — или всё уже сделано.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>


    </div>
  );
}
